import { Evidence2, UserContext } from '@eas/common-web';
import React, { useContext, useMemo } from 'react';
import { useSubTitle } from '../../components/evidence/evidence/sub-title-hook';

import { usePermission } from '../../components/permission/perm-evidence-hook';
import {
  EntityName,
  EvidenceAPI,
  IncludeOptions,
  PageKey,
  Permission,
  Messages,
} from '../../enums';
import {
  Me,
  MinimalDistances,
  PermissionsState,
  Exception,
} from '../../models';
import { useColumns } from './minimal-distances-columns';

import { useDatedEvidence } from '../../components/evidence/dated-evidence/dated-evidence';
import { MinimalDistancesFields } from './minimal-distances-fields';
import { useHistoryDescriptors } from './minimal-distances-history';
import { useValidationSchema } from './minimal-distances-schema';
import { EntityType } from '../../enums/entity-type';
import { ExportTags } from '../../enums/export-tags';
import { useSpecialFilters } from './minimal-distances-special-filters';
import { MinimalDistancesActionbar } from './minimal-distances-actionbar';
import { updateItemFactory } from '../../utils/custom-crud';

export function MinimalDistancesEvidence() {
  const { hasPermission } = useContext<UserContext<Me, PermissionsState>>(
    UserContext
  );
  const validationSchema = useValidationSchema();

  const permissions = usePermission<MinimalDistances>({
    options: {
      NEW: [Permission.MinimalDistances.MINIMAL_DISTANCES_CREATE_ALL],
      EDIT: [
        Permission.MinimalDistances.MINIMAL_DISTANCES_UPDATE_ALL,
        Permission.MinimalDistances.MINIMAL_DISTANCES_UPDATE_OWN,
      ],
      REMOVE: [],
      CHECK: [
        Permission.MinimalDistances.MINIMAL_DISTANCES_CREATE_ALL,
        Permission.MinimalDistances.MINIMAL_DISTANCES_UPDATE_ALL,
        Permission.MinimalDistances.MINIMAL_DISTANCES_UPDATE_OWN,
      ],
      SAVE: [
        Permission.MinimalDistances.MINIMAL_DISTANCES_CREATE_ALL,
        Permission.MinimalDistances.MINIMAL_DISTANCES_UPDATE_ALL,
        Permission.MinimalDistances.MINIMAL_DISTANCES_UPDATE_OWN,
      ],
      CLOSE: [
        Permission.MinimalDistances.MINIMAL_DISTANCES_CREATE_ALL,
        Permission.MinimalDistances.MINIMAL_DISTANCES_UPDATE_ALL,
        Permission.MinimalDistances.MINIMAL_DISTANCES_UPDATE_OWN,
      ],
    },
    dataCheck: {
      EDIT: (data) =>
        hasPermission(
          Permission.MinimalDistances.MINIMAL_DISTANCES_UPDATE_ALL
        ) ||
        hasPermission(
          Permission.MinimalDistances.MINIMAL_DISTANCES_UPDATE_OWN,
          {
            subjectId: data?.ovzFacility?.operator?.id,
          }
        ),
    },
  });

  const canSeeDeactivated = useMemo(
    () =>
      [
        Permission.MinimalDistances.MINIMAL_DISTANCES_REVALIDATE_ALL,
        Permission.MinimalDistances.MINIMAL_DISTANCES_REVALIDATE_OWN,
      ].some((perm) => hasPermission(perm, { searchInSubjectPerms: true })),
    [hasPermission]
  );

  const include = useMemo(
    () => (canSeeDeactivated ? [IncludeOptions.DEACTIVATED] : []),
    [canSeeDeactivated]
  );

  const historyDescriptors = useHistoryDescriptors();

  const evidence = useDatedEvidence<MinimalDistances>({
    version: 5,
    identifier: PageKey.MINIMAL_DISTANCES,
    noteEntityType: EntityType.MINIMAL_DISTANCES,
    apiProps: {
      url: EvidenceAPI.MINIMAL_DISTANCES,
      updateItem: updateItemFactory({
        postprocessCall: (data, response) => {
          if (!response.ok) {
            if (
              (data as Exception).code === 'OVZ_FACILITY_NOT_MINIMAL_DISTANCES'
            ) {
              throw {
                message:
                  Messages.MinimalDistancesSource.UPDATE
                    .OVZ_FACILITY_NOT_MINIMAL_DISTANCES[0],
              };
            }
          }
        },
      }),
    },
    tableProps: {
      columns: useColumns(),
      showColumn: (column) => {
        if (column === 'CREATED') {
          return false;
        }
        return true;
      },
      tableName: 'Přehled zdrojů - Minimální vzdálenosti',
      reportTag: ExportTags.MINIMAL_DISTANCES_SOURCE,
      defaultSorts: [],
      include,
      specialFilters: useSpecialFilters(),
      specialFiltersPerm:
        Permission.MinimalDistances.MINIMAL_DISTANCES_LIST_ALL,
    },
    detailProps: {
      entityName: EntityName.MINIMAL_DISTANCES,
      FieldsComponent: MinimalDistancesFields,

      hideGeneralFields: true,
      toolbarProps: {
        title: 'Minimální vzdálenosti',
        subtitle: useSubTitle<MinimalDistances>((data) => data?.name ?? ''),
        showBtn: permissions,
        ActionBar: MinimalDistancesActionbar,
      },
      validationSchema,
    },
    historyProps: {
      descriptors: historyDescriptors,
      permission: Permission.MinimalDistances.MINIMAL_DISTANCES_LIST_ALL,
    },
  });

  return <Evidence2 {...evidence} />;
}
